import { TulosteKieli } from "./TulosteKieli";
import { useLataus, LatausTila } from "./Lataus";
import { useKohdeSynkka } from "./Kohdekonteksti";
import { useCallback, useEffect, useState } from "react";
import { LinjaLinkki, KohdeLinkki } from "./Linkit";
import {
  api, ULOTTUVUUDET, type Kayttaja, type Kohde, type LahdeAukot, type Dokumentointivelka,
  type Ulottuvuus, VAIHEET, VAIHEEN_NIMI, type Vaihe } from "./api";
import { haeBrandi, type Brandi } from "./brandi";
import { Ohje } from "./Ohje";
import { useOsoiteHaku, lueHaku, haunLuku } from "./osoitehaku";
import { t, type KaannosAvain } from "./kieli";
import { nayttonimi } from "./nayttonimi";

/* Luovutusvalmius (ent. Laatu & aukot):
   1) Lähdedatan aukkoraportti (item 3) — järjestelmä nostaa lähtötiedon aukon
      esiin (isometri/suunnitelma/DN puuttuu). Aukko = peruste lisätyö-/
      viivästysvaateelle. TOISEN OSAPUOLEN velkaa meille.
   2) Dokumentointivelka-mittari (item 4) — luovutusaineiston valmius per linja;
      "aloittamatta" on itsessään näkyvä aukko. MEIDÄN velkaamme tilaajalle.

   Nolla tarkoittaa tällä sivulla neljää eri asiaa, eikä yksikään niistä ole
   sama kuin toinen: "ei yhtään vikaa", "ei yhtään valmista", "tarkistus ei ole
   ajossa" ja "ei mitattavissa". <Tunnusluku> erottaa ne rakenteella ja sanalla,
   ei värillä — ratkaiseva ero on nimittäjä. "0 walkdown OK" on merkityksetön,
   "0 / 6" ei ole. */

const pros = (n: number) => `${n} %`;

type Tila = "vika" | "puhdas" | "kesken" | "mittaamaton";

/* Luovutusvalmiuden joukot ihmisen sanoin. Jäsenyys tulee palvelimelta
   (`rivi.ulottuvuudet`, dokumentointivelka.ts) — tämä on vain nimi.
   Projekti-Hubin "Loki täydellinen 125 / 126" osoittaa tänne muodossa
   `?kohde=32&ulottuvuus=loki`, ja rivimäärän on oltava 125. */
const ULOTTUVUUDEN_NIMI: Record<Ulottuvuus, KaannosAvain> = {
  aloittamatta: "lau.ulot.aloittamatta",
  aloitettu: "lau.ulot.aloitettu",
  loki: "lau.ulot.loki",
  ndt: "lau.ulot.ndt",
  painekoe: "lau.ulot.painekoe",
  walkdown: "lau.ulot.walkdown",
  ei_punchia: "lau.ulot.ei_punchia",
  ndt_kesken: "lau.ulot.ndt_kesken",
  ndt_hylatty: "lau.ulot.ndt_hylatty",
  ndt_ei_vaadita: "lau.ulot.ndt_ei_vaadita",
  ndt_luokalle_ei_kirjattu: "lau.ulot.ndt_luokalle_ei_kirjattu",
  ndt_ei_asetettu: "lau.ulot.ndt_ei_asetettu",
  ndt_luokka_puuttuu: "lau.ulot.ndt_luokka_puuttuu",
  painekoe_kesken: "lau.ulot.painekoe_kesken",
  hitsaus_jaljella: "lau.ulot.hitsaus_jaljella",
  ndt_jaljella: "lau.ulot.ndt_jaljella",
  painekoe_jaljella: "lau.ulot.painekoe_jaljella",
  walkdown_jaljella: "lau.ulot.walkdown_jaljella",
  punchit_jaljella: "lau.ulot.punchit_jaljella",
  primaari_jaljella: "lau.ulot.primaari_jaljella",
  sekundaari_jaljella: "lau.ulot.sekundaari_jaljella",
  kannakointi_ei_tiedossa: "lau.ulot.kannakointi_ei_tiedossa",
  kannakointi_ristiriita: "lau.ulot.kannakointi_ristiriita",
  walkdown_poikkeama: "lau.ulot.walkdown_poikkeama",
  luovutusliite_puuttuu: "lau.ulot.luovutusliite_puuttuu",
  isometri_korvattu: "lau.ulot.isometri_korvattu",
  painekoe_ennen_ndt: "lau.ulot.painekoe_ennen_ndt",
  patevyys_estaa: "lau.ulot.patevyys_estaa",
  patevyys_ei_tiedossa: "lau.ulot.patevyys_ei_tiedossa",
};
/* T683: pätevyyden tilat (269) erittelysarakkeessa. */
const PV_TILA = {
  ei: "lau.pv_tila.ei", vanhentunut: "lau.pv_tila.vanhentunut", myonnetty_myohemmin: "lau.pv_tila.myonnetty_myohemmin",
  varmentamaton: "lau.pv_tila.varmentamaton", tuntematon: "lau.pv_tila.tuntematon",
} as const satisfies Record<string, KaannosAvain>;
const lueVaihe = (p: URLSearchParams): Vaihe | "" => {
  const v = p.get("vaihe") as Vaihe | null;
  return v && (VAIHEET as readonly string[]).includes(v) ? v : "";
};
const lueUlottuvuus = (p: URLSearchParams): Ulottuvuus | "" => {
  const u = p.get("ulottuvuus") as Ulottuvuus | null;
  return u && (ULOTTUVUUDET as readonly string[]).includes(u) ? u : "";
};

/* G4 (19.9.2026): seitsemän tilaa, ja jokaisella oma sanansa ruudulla.
   Neljä niistä oli 18.9. asti sama arvo «ei_vaatimusta» — ja kaksi niistä
   neljästä tarkoittaa vastakkaisia asioita: «ei vielä asetettu» on tieto jota
   ei ole, «ei vaadita» on päätös. Sanat yhdessä paikassa: solu, sen title ja
   suodattimen selite lukevat saman. */
const NDT_SANA: Record<string, KaannosAvain> = {
  valmis: "lau.ndt_sana.valmis",
  kesken: "lau.ndt_sana.kesken",
  aloittamatta: "lau.ndt_sana.aloittamatta",
  ei_vaadita: "lau.ndt_sana.ei_vaadita",
  luokalle_ei_kirjattu: "lau.ndt_sana.luokalle_ei_kirjattu",
  ei_asetettu: "lau.ndt_sana.ei_asetettu",
  luokka_puuttuu: "lau.ndt_sana.luokka_puuttuu",
};
const NDT_SELITE: Record<string, KaannosAvain> = {
  valmis: "lau.ndt_sel.valmis",
  kesken: "lau.ndt_sel.kesken",
  aloittamatta: "lau.ndt_sel.aloittamatta",
  ei_vaadita: "lau.ndt_sel.ei_vaadita",
  luokalle_ei_kirjattu: "lau.ndt_sel.luokalle_ei_kirjattu",
  ei_asetettu: "lau.ndt_sel.ei_asetettu",
  luokka_puuttuu: "lau.ndt_sel.luokka_puuttuu",
};

export default function LaatuAukot({ kayttaja, osoiteHaku, asetaOsoiteHaku }: {
  kayttaja: Kayttaja;
  /** Osoitteen kyselyosa: `kohde=<id>&ulottuvuus=<joukko>`, ks. osoitehaku.ts. */
  osoiteHaku?: string;
  asetaOsoiteHaku?: (haku: string) => void;
}) {
  const [vaateKieli, setVaateKieli] = useState("");
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const alku = lueHaku(osoiteHaku);
  const [kohde, setKohde] = useState<number | "">(() => haunLuku(alku, "kohde"));
  // T426: yläpalkin kohdevalinta ohjaa; osoitteen ?kohde= vaihtaa yläpalkin.
  const { ylapalkissa, synkattu } = useKohdeSynkka(kohde, setKohde,
    { osoitteenKohde: haunLuku(lueHaku(osoiteHaku), "kohde"), kaikkiSallittu: true });
  const [ulottuvuus, setUlottuvuus] = useState<Ulottuvuus | "">(() => lueUlottuvuus(alku));
  /** T397: seuraava vaihe. Oma suodatin, koska vaihe on yksi arvo per linja eikä joukkojäsenyys. */
  const [vaihe, setVaihe] = useState<Vaihe | "">(() => lueVaihe(alku));
  const [aukot, setAukot] = useState<LahdeAukot | null>(null);
  const [velka, setVelka] = useState<Dokumentointivelka | null>(null);
  const [brandi, setBrandi] = useState<Brandi | null>(null);

  useOsoiteHaku(osoiteHaku, asetaOsoiteHaku, { kohde, ulottuvuus, vaihe },
    /* 17.10.2026 (Toni klo 12.05): osoite ilman kohdetta ei ylikirjoita yläpalkin kohdetta — valikosta avattuna mountin
       osoitesynkka ajoi yläpalkin koukun jälkeen ja palautti kohteeksi «kaikki», jolloin näkymä ja yläpalkki jäivät eri mieltä. */
    (p) => { if (p.has("kohde")) setKohde(haunLuku(p, "kohde")); setUlottuvuus(lueUlottuvuus(p)); setVaihe(lueVaihe(p)); });

  const klKohteet = useLataus();
  useEffect(() => { klKohteet.seuraa(api.kohteet().then(setKohteet)); }, [klKohteet.kerta]);
  useEffect(() => { haeBrandi().then(setBrandi).catch(() => {}); }, []);
  const lataa = useCallback(() => {
    // T473 (10.10.2026): ei hakua ennen kuin kohde vastaa yläpalkkia — muuten ensin «kaikki kohteet» (N-2406 ensin).
    if (!synkattu) return;
    const k = kohde === "" ? undefined : kohde;
    api.lahdeaukot(k).then(setAukot).catch(() => setAukot(null));
    api.dokumentointivelka(k).then(setVelka).catch(() => setVelka(null));
  }, [kohde, synkattu]);
  useEffect(() => { lataa(); }, [lataa]);

  /* Tilanneleima. Hetki on palvelimen — sivu on vaadeaineistoa, ja vaade
     ilman päiväystä on väite tuntemattomasta hetkestä. Selaimen kello ei
     kelpaa siihen. Talon nimi tulee brändistä (sama lähde kuin
     brandi.raportti_ylatunnisteella). */
  const valittu = kohteet.find((k) => k.id === kohde);
  /* T464 (Toni 10.10.2026): tilaajalle lohko on neutraali «Odottaa tilaajalta», ilman vaade-Exceliä (reitti ei ole
     tilaajan sallittujen listalla), ja urakoitsija voi piilottaa sen kohteittain (221). Sama ehto kuin API:n
     onAsiakastunnus: laji ulkopuolinen eikä tarkastuslaitos. */
  const asiakas = kayttaja.laji === "ulkopuolinen" && !kayttaja.roolit.includes("tarkastaja");
  const aukotPiilossa = asiakas && valittu?.nayta_lahtoaukot_tilaajalle === false;
  /* Toni 25.10.2026 («korjaa lähdeaukkojen tilaajan tekstit»; 10.10. päätös: neutraali, asiakkaan näkökulmasta kirjoitettu versio):
     tunnusluvut, merkit ja puuttuvien linjojen otsikko tilaajalle omilla avaimillaan — urakoitsijan sanasto («tehty ILMAN
     lähtötietoa», «vahvin lisätyö-/viivästysperuste») jää urakoitsijalle. Merkit tilaajalle ilman vikaväriä. */
  const tl = (urakoitsija: KaannosAvain, tilaaja: KaannosAvain) => (asiakas ? tilaaja : urakoitsija);
  const hetki = velka?.hetki ?? aukot?.hetki ?? null;

  /* Suodatettu rivijoukko KOKO listasta, ei 400:n otoksesta: luku on
     joukon koko, taulukko on sivu. Jäsenyys on palvelimen laskema. */
  const velkaRivit = velka
    ? (ulottuvuus ? velka.linjat.filter((r) => r.ulottuvuudet.includes(ulottuvuus)) : velka.linjat)
        .filter((r) => !vaihe || r.seuraava_vaihe === vaihe)
    : null;
  const leima = hetki && [
    t("lau.leima_tilanne", { pvm: new Date(hetki).toLocaleDateString("fi-FI"), klo: new Date(hetki).toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" }) }),
    brandi?.nimi ?? "Sampo Industrial",
    valittu ? t("lau.leima_kohde", { tunnus: valittu.tunnus }) : t("lau.leima_kaikki"),
    velka ? t("lau.n_linjaa", { n: velka.summary.linjoja }) : null,
  ].filter(Boolean).join(" · ");

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.luovutusvalmius")}</h1></div>
        {leima && <div className="head__leima">{leima}</div>}
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />

      {/* Ohje ylös (Toni 24.9.2026). «Kuka» = /api/lahdeaukot(+/vienti) ja /api/dokumentointivelka: vaadi + moduuli
          hitsausloki + kohderajaus, ei roolirajaa; vienti kirjataan vientilokiin. Valmiuden määritelmä siirtyi tähän
          alalaidan selitteestä. */}
      <Ohje
        kuka={<>{t(asiakas ? "lau.ohje.kuka_tilaaja" : "lau.ohje.kuka")}</>}
        luvut={<>{t(asiakas ? "lau.ohje.luvut_tilaaja" : "lau.ohje.luvut")}</>} />

      <div style={{ margin: "0 0 16px" }}>
        {!ylapalkissa && <label className="dim" style={{ fontSize: 13 }}>{t("kuittaus.kohde")}{" "}
          <select value={kohde} onChange={(e) => setKohde(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("pu.kaikki_kohteet")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select>
        </label>}
      </div>

      {/* ── Lähdedatan aukot ── */}
      {!aukotPiilossa && <section className="panel" style={{ marginBottom: 20 }} data-lahtoaukot={asiakas ? "tilaaja" : "urakoitsija"}>
        <div className="panel__head"><h2>{t("lau.lahdeaukot")}</h2>
          <span style={{ display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {/* Vaihe c (209): vaade lähtee tilaajalle — kieli valitaan tulostushetkellä, oletus kohteen kieli. */}
            {!asiakas && <>
            <TulosteKieli arvo={vaateKieli} muuta={setVaateKieli} />
            <a className="nappi nappi--kevyt" style={{ textDecoration: "none" }} data-vaade-vienti={vaateKieli}
               href={api.lahdeaukotVientiOsoite(kohde === "" ? undefined : kohde, vaateKieli || undefined)}
               title={t("lau.vie_title")}>
              {t("lau.vie")}
            </a>
            </>}
          </span></div>
        <div className="panel__vyo">
          {asiakas
            ? <><b>{t("lau.vyo_tilaajalta")}</b> <span>{t("lau.vyo_tilaajalta_selite")}</span></>
            : <><b>{t("lau.vyo_toinen")}</b> <span>{t("lau.vyo_toinen_selite")}</span></>}
        </div>
        {aukot && (
          <>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", padding: "12px 0" }}>
              <Vikaluku arvo={aukot.summary.tehty_ilman_suunnitelmaa} teksti={t(tl("lau.tehty_ilman", "lau.t.tehty_ilman"))} />
              {/* P&ID-tarkistukset: ilman linjalistaa nämä eivät ole nollia
                  vaan tuntemattomia. Ennen ne eivät renderöityneet lainkaan —
                  ajamaton tarkistus oli näkymätön. */}
              {aukot.onPid
                ? <Vikaluku arvo={aukot.summary.dn_ristiriita} teksti={t(tl("lau.dn_ristiriita_pid", "lau.t.dn_ristiriita_pid"))} />
                : <Tunnusluku tila="mittaamaton" teksti={t(tl("lau.dn_ristiriita_pid", "lau.t.dn_ristiriita_pid"))} selite={t("lau.ei_linjalistaa")} />}
              {aukot.onPid
                ? <Vikaluku arvo={aukot.summary.puuttuu_pid} teksti={t(tl("lau.puuttuu_pid", "lau.t.puuttuu_pid"))} />
                : <Tunnusluku tila="mittaamaton" teksti={t(tl("lau.puuttuu_pid", "lau.t.puuttuu_pid"))} selite={t("lau.ei_linjalistaa")} />}
              <Vikaluku arvo={aukot.summary.ei_lahtotietoa} teksti={t(tl("lau.ei_lahtotietoa_bom", "lau.t.ei_lahtotietoa_bom"))} />
              <Vikaluku arvo={aukot.summary.isometri_puuttuu} teksti={t(tl("lau.isometri_bom_puuttuu", "lau.t.isometri_bom_puuttuu"))} />
              <Vikaluku arvo={aukot.summary.nimi_vs_kanta} teksti={t(tl("lau.nimi_vs_kanta", "lau.t.nimi_vs_kanta"))} />
              <Vikaluku arvo={aukot.summary.dn_epaselva} teksti={t(tl("lau.dn_epaselva", "lau.t.dn_epaselva"))} />
            </div>
            {!aukot.onPid && <p className="dim" style={{ marginTop: -4 }}>
              {t("lau.pid_ei_ajossa")}</p>}

            {aukot.linjat.length === 0 && <p className="dim">{t("lau.ei_aukkoja")}</p>}
            {aukot.linjat.length > 0 && (
              <div className="scroll">
                <table className="tbl" style={{ fontSize: 13 }}>
                  <thead><tr><th>{t("nippu.linja")}</th><th>{t("lau.media")}</th><th>{t("kuittaus.kohde")}</th><th>{t("lau.aukot")}</th></tr></thead>
                  <tbody>
                    {aukot.linjat.slice(0, 400).map((r) => (
                      <tr key={r.id}>
                        <td className="mono"><LinjaLinkki tunnus={r.tunnus} /></td>
                        <td className="mono">{r.media ?? ""}</td>
                        <td className="dim"><KohdeLinkki tunnus={r.kohde} /></td>
                        <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {r.tehty_ilman_suunnitelmaa && <Merkki teksti={t(tl("lau.m_tehty_ilman", "lau.t.m_tehty_ilman"))} paha={!asiakas} />}
                          {r.dn_ristiriita && <Merkki teksti={t(tl("lau.m_dn_ristiriita", "lau.t.m_dn_ristiriita"), { dn: r.pid_dn ?? "" })} paha={!asiakas} />}
                          {r.ei_lahtotietoa && !r.tehty_ilman_suunnitelmaa && <Merkki teksti={t(tl("lau.m_ei_lahtotietoa", "lau.t.m_ei_lahtotietoa"))} />}
                          {r.isometri_puuttuu && !r.ei_lahtotietoa && <Merkki teksti={t(tl("lau.m_isometri", "lau.t.m_isometri"))} />}
                          {r.nimi_vs_kanta && <Merkki teksti={t(tl("lau.m_nimi_sanoo", "lau.t.m_nimi_sanoo"), { koko: r.nimen_koko ?? "" })} paha={!asiakas} />}
                          {r.dn_epaselva && <Merkki teksti={t(tl("lau.m_dn_epaselva", "lau.t.m_dn_epaselva"))} />}
                          {r.isometri_korvattu && <span data-lahdeaukko-korvattu={r.isometri_korvattu}>
                            <Merkki teksti={t("pu.korvattu_n", { laji: nayttonimi("aineistolaji", r.isometri_korvattu) })} /></span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {aukot.linjat.length > 400 && <p className="dim" >{t("lau.naytetaan_400", { n: aukot.linjat.length })}</p>}
              </div>
            )}

            {aukot.onPid && aukot.puuttuvat.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h3 style={{ fontSize: 14, margin: "0 0 6px" }}>{t(tl("lau.puuttuvat_linjat", "lau.t.puuttuvat_linjat"), { n: aukot.puuttuvat.length })}</h3>
                <p className="dim" style={{ margin: "0 0 8px" }}>{t(tl("lau.puuttuvat_selite", "lau.t.puuttuvat_selite"))}</p>
                <div className="scroll">
                  <table className="tbl" style={{ fontSize: 13 }}>
                    <thead><tr><th>{t("lau.pid_linja")}</th><th>{t("lau.media")}</th><th>DN</th><th>{t("kuittaus.kohde")}</th></tr></thead>
                    <tbody>
                      {aukot.puuttuvat.slice(0, 200).map((p) => (
                        <tr key={p.tunnus}><td className="mono">{p.tunnus}</td><td className="mono">{p.media ?? ""}</td>
                          <td className="mono">{p.dn ?? ""}</td><td className="dim">{p.kohde}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </section>}

      {/* ── Dokumentointivelka ── */}
      <section className="panel">
        {/* T464 jatko (pääChatin toimeksianto Tonin välittämänä 11.10.2026): tilaajalle neutraali «Luovutusaineisto — vielä puuttuu». */}
        <div className="panel__head"><h2>{t(asiakas ? "lau.luovutusaineisto" : "lau.dokvelka")}</h2>
          <span className="hint">{t("lau.dokvelka_hint")}</span></div>
        <div className="panel__vyo">
          {asiakas
            ? <><b>{t("lau.vyo_puuttuu")}</b> <span>{t("lau.vyo_puuttuu_selite")}</span></>
            : <><b>{t("lau.vyo_meidan")}</b> <span>{t("lau.vyo_meidan_selite")}</span></>}
        </div>
        {velka && (
          <>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", padding: "12px 0" }}>
              {/* Pari, ei yksi luku. "Miten aloitetut etenevät" ja "moniko on
                  aloittamatta" ovat eri kysymyksiä, eikä yksi keskiarvo
                  vastaa molempiin valehtelematta kummin päin tahansa. */}
              {velka.summary.keskivalmius_aloitetut === null
                ? <Tunnusluku tila="mittaamaton" teksti={t("lau.keskivalmius")} selite={t("lau.ei_aloitettuja")} />
                : <Tunnusluku tila="kesken" arvo={pros(velka.summary.keskivalmius_aloitetut)}
                    teksti={t("lau.keskivalmius")} selite={t("lau.aloitetusta", { n: velka.summary.aloitettuja })} />}
              <Tunnusluku tila={velka.summary.aloittamatta > 0 ? "vika" : "puhdas"}
                arvo={velka.summary.aloittamatta} nimittaja={velka.summary.linjoja}
                teksti={t("lau.ulot.aloittamatta")}
                selite={velka.summary.aloittamatta > 0 ? t("lau.ei_prosenttia") : undefined} />
              <Tunnusluku tila="kesken" arvo={velka.summary.valmiita} nimittaja={velka.summary.linjoja} teksti={t("lau.valmista_linjaa")} />
              <Tunnusluku tila="kesken" arvo={velka.summary.ndt} nimittaja={velka.summary.linjoja} teksti={t("lau.ndt_tehty")} />
              <Tunnusluku tila="kesken" arvo={velka.summary.painekoe} nimittaja={velka.summary.linjoja} teksti={t("lau.painekoe")} />
              <Tunnusluku tila="kesken" arvo={velka.summary.walkdown} nimittaja={velka.summary.linjoja} teksti={t("lau.walkdown_ok")} />
              {/* T655 (Toni 25.10.2026): korvatut omana lukunaan — eivät estä luovutusta, eivätkä sekoitu linjaan jolle ei ole tehty mitään. */}
              <span data-isometri-korvattu={velka.linjat.filter((r) => r.ulottuvuudet.includes("isometri_korvattu")).length}>
                <Tunnusluku tila="kesken" arvo={velka.linjat.filter((r) => r.ulottuvuudet.includes("isometri_korvattu")).length}
                  nimittaja={velka.summary.linjoja} teksti={t("lau.ulot.isometri_korvattu")} /></span>
              {/* T677 B (Toni 27.10.2026 klo 09.25): järjestysrike laatuhavaintona — ei estä mitään; luku avaa listan. Tilaajalle
                  neutraali sanamuoto. Näkyy vain kun rikkeitä on. */}
              {(() => { const n = velka.linjat.filter((r) => r.ulottuvuudet.includes("painekoe_ennen_ndt")).length;
                return n > 0 ? (
                  <button type="button" className="tunnusluku-nappi" data-painekoe-ennen-ndt={n} style={{ all: "unset", cursor: "pointer" }}
                          onClick={() => setUlottuvuus("painekoe_ennen_ndt")} title={t("lau.avaa_lista")}>
                    <Tunnusluku tila="kesken" arvo={n} nimittaja={velka.summary.linjoja}
                      teksti={t(tl("lau.ulot.painekoe_ennen_ndt", "lau.ulot.painekoe_ennen_ndt_tilaaja"))} />
                  </button>) : null; })()}
              {/* T683 (Toni 28.10.2026): pätevyys hitsauspäivänä. Pysäyttävät ja hidastavat omina lukuinaan; luku avaa listan.
                  Ei mitattu (kenttä puuttuu) = ei lukua lainkaan, ei nollaa. */}
              {velka.patevyys && (["patevyys_estaa", "patevyys_ei_tiedossa"] as const).map((u) => {
                const n = velka.linjat.filter((r) => r.ulottuvuudet.includes(u)).length;
                return (
                  <button key={u} type="button" className="tunnusluku-nappi" data-patevyys-linjat={u} data-n={n}
                          style={{ all: "unset", cursor: "pointer" }} onClick={() => setUlottuvuus(u)} title={t("lau.avaa_lista")}>
                    <Tunnusluku tila={n === 0 ? "puhdas" : u === "patevyys_estaa" ? "vika" : "kesken"} arvo={n}
                      nimittaja={velka.summary.aloitettuja} teksti={t(ULOTTUVUUDEN_NIMI[u])} />
                  </button>);
              })}
            </div>
            {/* Joukkosuodatin. ⚠ Yllä olevat tunnusluvut (NDT, painekoe,
                walkdown) lasketaan KAIKISTA linjoista; Hubin viisi riviä
                ja tämä suodatin lasketaan ALOITETUISTA. Eri nimittäjä on
                sanottu suodattimen nimessä, jottei lukija vertaa
                keskenään lukuja jotka eivät mittaa samaa joukkoa. */}
            <div className="suodatinrivi" style={{ padding: "0 0 10px" }}>
              <label className="dim" style={{ fontSize: 13 }}>{t("lau.nayta")}{" "}
                <select value={ulottuvuus} onChange={(e) => setUlottuvuus(e.target.value as Ulottuvuus | "")}>
                  <option value="">{t("lau.kaikki_linjat")}</option>
                  {ULOTTUVUUDET.map((u) => <option key={u} value={u}>{t(ULOTTUVUUDEN_NIMI[u])}</option>)}
                </select>
              </label>
              {/* T397 (Toni 30.9.2026): seuraava vaihe on yksi arvo per linja — Hubin «Seuraava vaihe» -luvut
                  osoittavat tänne muodossa `?kohde=&vaihe=`, ja rivimäärän on oltava sama. */}
              <label className="dim" style={{ fontSize: 13 }}>{t("lau.seuraava_vaihe")}{" "}
                <select value={vaihe} data-suodatin="vaihe" onChange={(e) => setVaihe(e.target.value as Vaihe | "")}>
                  <option value="">{t("lau.kaikki")}</option>
                  {VAIHEET.map((v) => <option key={v} value={v}>{VAIHEEN_NIMI[v]}</option>)}
                </select>
              </label>
              {velkaRivit && (
                <span className="dim" style={{ fontSize: 13 }}>
                  <b data-rivit={velkaRivit.length} data-joukko="luovutusvalmius"
                     style={{ fontVariantNumeric: "tabular-nums" }}>{velkaRivit.length}</b>{" "}
                  {t("lau.linjaa")}{ulottuvuus ? ` · ${t(ULOTTUVUUDEN_NIMI[ulottuvuus])}` : ""}{vaihe ? ` · ${t("lau.seuraava_vaihe_pieni", { vaihe: VAIHEEN_NIMI[vaihe] })}` : ""}
                </span>
              )}
            </div>
            <div className="scroll">
              <table className="tbl" style={{ fontSize: 13 }}>
                <thead><tr><th>{t("nippu.linja")}</th><th>{t("kuittaus.kohde")}</th><th style={{ width: 160 }}>{t("lau.valmius")}</th>
                  <th style={{ textAlign: "center" }}>{t("lau.loki")}</th><th style={{ textAlign: "center" }}>NDT</th>
                  <th style={{ textAlign: "center" }}>{t("lau.painekoe_sarake")}</th><th style={{ textAlign: "center" }}>{t("lau.walkdown")}</th>
                  <th style={{ textAlign: "center" }}>{t("lau.punchit")}</th></tr></thead>
                <tbody>
                  {(velkaRivit ?? []).slice(0, 400).map((r) => (
                    <tr key={r.id} className={r.aloittamatta ? "rivi--aloittamatta" : undefined}>
                      <td className="mono"><LinjaLinkki tunnus={r.tunnus} />
                        {r.aloittamatta && <span className="merkki--aloittamatta">{t("lau.aloittamatta_merkki")}</span>}</td>
                      <td className="dim"><KohdeLinkki tunnus={r.kohde} /></td>
                      <td>{r.valmius === null
                        ? <span className="ei-mitattavissa">{t("jat.ei_mitattavissa")}</span>
                        : <Palkki arvo={r.valmius} />}</td>
                      <td style={{ textAlign: "center" }}>{r.loki ? "✓" : `${r.hitsattu}/${r.hitseja}`}</td>
                      {/* D2 (18.9.2026): kolme tilaa, ei kahta. «Kesken» ja
                          «aloittamatta» ovat eri työtä — toinen odottaa jatkoa,
                          toinen alkua — ja ennen tätä molemmat piirtyivät
                          viivana. «Ei vaatimusta» on neljäs eikä se ole kunnossa:
                          kattavuutta ei voi laskea, ja se sanotaan. */}
                      <td style={{ textAlign: "center" }}
                          data-ndt={r.ndt_tila}
                          title={(NDT_SELITE[r.ndt_tila] ? t(NDT_SELITE[r.ndt_tila]!) : "")
                            + (r.ndt_tila === "luokalle_ei_kirjattu" && r.ped_luokka
                               ? ` ${t("lau.luokka", { luokka: r.ped_luokka })}` : "")
                            /* 137: päätös näkyy siellä missä se luetaan. «Ei vaadita»
                               täyttää ulottuvuuden ilman tarkastuksia, ja luovutuksessa
                               kysytään kuka niin päätti ja milloin. */
                            + (r.ndt_tila === "ei_vaadita" && r.ndt_k == null
                               ? ` ${t("lau.kirjasi", { kuka: r.ndt_paatos ?? t("lau.ei_tiedossa") })}` : "")
                            /* T514 ehto b: linjan K voittaa PED:n — lähde näkyy, oletus ei näy pelkkänä ✓:nä (Toni 21.10. sääntö 3). */
                            + (r.ndt_k != null ? ` ${t("lau.ndt_k_selite", { k: r.ndt_k })}` : "")}
                          data-ndt-k={r.ndt_k ?? undefined}>
                        {r.ndt_tila === "valmis" ? "✓"
                          : r.ndt_tila === "kesken" || r.ndt_tila === "aloittamatta"
                            ? <span className="merkki merkki--varoitus">{t(NDT_SANA[r.ndt_tila]!)}</span>
                          : r.ndt_tila === "ei_vaadita"
                            ? <span className="dim">{t(NDT_SANA[r.ndt_tila]!)}</span>
                          /* Kolme aukkoa: ei päätöksiä vaan puuttuvaa lähtötietoa.
                             «Luokalle ei kirjattu» sanoo myös MINKÄ luokan. */
                          : <span className="merkki merkki--vika">
                              {NDT_SANA[r.ndt_tila] ? t(NDT_SANA[r.ndt_tila]!) : r.ndt_tila}
                              {r.ndt_tila === "luokalle_ei_kirjattu" && r.ped_luokka ? ` (${r.ped_luokka})` : ""}
                            </span>}
                        {r.ndt_k != null && <span className="dim"> {t("lau.ndt_k_merkki", { k: r.ndt_k })}</span>}
                      </td>
                      <td style={{ textAlign: "center" }}
                          title={r.painekoe_on ? t("lau.pk_title_ok")
                            : r.painekoe_kesken ? t("lau.pk_title_kesken")
                            : t("lau.pk_title_ei")}>
                        {r.painekoe_on ? "✓"
                          : r.painekoe_kesken ? <span className="merkki merkki--varoitus">{t("lau.ndt_sana.kesken")}</span>
                          : "–"}
                      </td>
                      <td style={{ textAlign: "center" }}>{r.walkdown_tila === "hyvaksytty" ? "✓" : (r.walkdown_tila ? "…" : "–")}</td>
                      {/* Aloittamattomalla linjalla ei ole työtä josta punch
                          voisi syntyä. "✓" väitti että laatukriteeri täyttyy. */}
                      <td style={{ textAlign: "center" }}>{r.aloittamatta ? "–" : (r.avoimia_puncheja > 0
                        ? <span style={{ color: "var(--puna)" }}>{r.avoimia_puncheja}</span> : "✓")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {velkaRivit && velkaRivit.length === 0 && <p className="dim">{t("lau.ei_linjoja")}</p>}
              {velkaRivit && velkaRivit.length > 400 && <p className="dim" >{t("lau.naytetaan_400", { n: velkaRivit.length })}</p>}
            </div>
            <p className="dim" style={{ marginTop: 8 }}>
              {velka.summary.keskivalmius_aloitetut === null
                ? <>{t("lau.ei_keskiarvoa")}</>
                : <>{t("lau.keskiarvo_lause", { p: pros(velka.summary.keskivalmius_aloitetut), n: velka.summary.aloitettuja, a: velka.summary.aloittamatta, m: velka.summary.linjoja })}</>}
            </p>
            {/* T683: dokumentointivelka hitsaajittain. Koodi, ei nimeä (sivu näkyy myös tilaajalle). */}
            {velka.patevyys && (
              <div data-patevyysvelka={velka.patevyys.length} style={{ marginTop: 16 }}>
                <h3 style={{ fontSize: 15, margin: "0 0 4px" }}>{t("lau.pv_otsikko")}</h3>
                <p className="dim" style={{ margin: "0 0 8px" }}>{t("lau.pv_selite")}</p>
                {velka.patevyys.length === 0
                  ? <p className="dim">{t("lau.pv_ei_velkaa")}</p>
                  : <div className="scroll"><table className="tbl" style={{ fontSize: 13 }}>
                      <thead><tr><th>{t("lau.pv_hitsaaja")}</th>
                        <th style={{ textAlign: "right" }}>{t("lau.pv_estaa")}</th><th style={{ textAlign: "right" }}>{t("lau.pv_ei_tiedossa")}</th>
                        <th>{t("lau.pv_erittely")}</th><th style={{ textAlign: "right" }}>{t("lau.pv_linjoja")}</th></tr></thead>
                      <tbody>{velka.patevyys.map((p) => (
                        <tr key={`${p.henkilo_id ?? "-"}|${p.koodi ?? "-"}`} data-pv-hitsaaja={p.koodi ?? ""}>
                          <td className="mono">{p.koodi ?? <span className="dim">{t("lau.pv_ei_hitsaajaa")}</span>}
                            {p.henkilo_id === null && p.koodi && <span className="dim"> · {t("lau.pv_tunnistamaton")}</span>}</td>
                          <td style={{ textAlign: "right" }}>{p.estaa > 0
                            ? <span className="merkki merkki--vika">{p.estaa}</span> : <span className="dim">0</span>}</td>
                          <td style={{ textAlign: "right" }}>{p.ei_tiedossa > 0
                            ? <span className="merkki merkki--varoitus">{p.ei_tiedossa}</span> : <span className="dim">0</span>}</td>
                          <td className="dim">{([["ei", p.ei], ["vanhentunut", p.vanhentunut], ["myonnetty_myohemmin", p.myonnetty_myohemmin],
                              ["varmentamaton", p.varmentamaton], ["tuntematon", p.tuntematon]] as const)
                              .filter(([, n]) => n > 0).map(([x, n]) => `${t(PV_TILA[x])} ${n}`).join(" · ")}</td>
                          <td style={{ textAlign: "right" }}>{p.linjoja_estaa > 0 ? `${p.linjoja_estaa} / ${p.linjoja}` : p.linjoja}</td>
                        </tr>))}</tbody>
                    </table></div>}
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

/** Vikalaskuri: > 0 on vika, 0 on puhdas — eikä puhdas näytä numeroa. */
function Vikaluku({ arvo, teksti }: { arvo: number; teksti: string }) {
  return arvo > 0
    ? <Tunnusluku tila="vika" arvo={arvo} teksti={teksti} />
    : <Tunnusluku tila="puhdas" teksti={teksti} />;
}

/**
 * Tunnusluvun neljä tilaa.
 *
 * `puhdas` menettää numeron tarkoituksella: numero pyytää vertailua,
 * teksti ei. `kesken` kantaa aina nimittäjän — se poistaa nollan
 * epäselvyyden ilman yhtäkään uutta väriä. `mittaamaton` näyttää
 * viivan ja sanoo syyn: se ei ole nolla vaan tuntematon.
 */
function Tunnusluku({ tila, arvo, nimittaja, teksti, selite }: {
  tila: Tila; arvo?: number | string; nimittaja?: number; teksti: string; selite?: string;
}) {
  if (tila === "puhdas") {
    return (
      <span className="tunnusluku tunnusluku--puhdas">
        <svg className="tunnusluku__ok" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M2 8.5 L6 12.5 L14 3.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
        </svg>
        <span className="tunnusluku__teksti">{t("lau.ei_yhtaan")}</span>
        <span className="tunnusluku__selite">{teksti}</span>
      </span>
    );
  }
  return (
    <span className={`tunnusluku tunnusluku--${tila}`}>
      <b className="tunnusluku__arvo">{tila === "mittaamaton" ? "–" : arvo}</b>
      {nimittaja !== undefined && tila !== "mittaamaton" &&
        <span className="tunnusluku__nimittaja">/ {nimittaja}</span>}
      <span className="tunnusluku__teksti">{teksti}</span>
      {selite && <span className="tunnusluku__selite">{selite}</span>}
    </span>
  );
}

function Merkki({ teksti, paha = false }: { teksti: string; paha?: boolean }) {
  return <span style={{ fontSize: 11, padding: "2px 7px", border: "1px solid var(--viiva)",
    color: paha ? "#fff" : "var(--muste)", background: paha ? "var(--puna)" : "transparent",
    fontWeight: paha ? 700 : 400 }}>{teksti}</span>;
}

function Palkki({ arvo }: { arvo: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: "var(--viiva)", minWidth: 80 }}>
        <div style={{ width: `${arvo}%`, height: "100%",
          background: arvo === 100 ? "var(--korostus-700)" : "var(--muste)" }} />
      </div>
      <span className="mono" style={{ fontSize: 12, width: 34, textAlign: "right" }}>{pros(arvo)}</span>
    </div>
  );
}
